import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, CreditCard, DollarSign, Gift, CheckCircle,
  Users, Globe, Utensils, Video, Music, BookOpen,
  Home, Truck, Megaphone, Church, Sparkles, PawPrint, Eye, EyeOff
} from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import ScriptureHeader from '../components/ScriptureHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const donationAmounts = [25, 50, 100, 250, 500];

// Ministry options for donation designation
const MINISTRY_OPTIONS = [
  { 
    id: 'general', 
    name: 'General Fund', 
    nameEs: 'Fondo General',
    icon: Church, 
    description: 'Support wherever needed most',
    color: 'bg-burgundy/10 text-burgundy',
    org: 'lfm',
    taxStatus: '508(c)(1)(a)'
  },
  { 
    id: 'media', 
    name: 'Media Ministry', 
    nameEs: 'Ministerio de Medios',
    icon: Video, 
    description: 'Videos, podcasts & livestream',
    color: 'bg-purple/10 text-purple',
    org: 'lfm',
    taxStatus: '508(c)(1)(a)'
  },
  { 
    id: 'worship', 
    name: 'Worship & Music', 
    nameEs: 'Adoración y Música',
    icon: Music, 
    description: 'Instruments & sound equipment',
    color: 'bg-indigo-100 text-indigo-600',
    org: 'lfm',
    taxStatus: '508(c)(1)(a)'
  },
  { 
    id: 'building', 
    name: 'Building Fund', 
    nameEs: 'Fondo de Edificio',
    icon: Home, 
    description: 'Facility maintenance & expansion',
    color: 'bg-amber-100 text-amber-600',
    org: 'lfm',
    taxStatus: '508(c)(1)(a)'
  },
  { 
    id: 'education', 
    name: 'Christian Education', 
    nameEs: 'Educación Cristiana',
    icon: BookOpen, 
    description: 'Bible studies & training',
    color: 'bg-teal-100 text-teal-600',
    org: 'lfm',
    taxStatus: '508(c)(1)(a)'
  },
  { 
    id: 'offering', 
    name: 'Offering Box', 
    nameEs: 'Caja de Ofrenda',
    icon: Gift, 
    description: 'Weekly tithes & offerings',
    color: 'bg-amber-100 text-amber-700',
    org: 'lfm',
    taxStatus: '508(c)(1)(a)'
  },
  { 
    id: 'guest_minister', 
    name: 'Guest Minister Blessing', 
    nameEs: 'Bendición para Ministro Invitado',
    icon: Heart, 
    description: 'Bless our visiting ministers',
    color: 'bg-rose-100 text-rose-600',
    org: 'lfm',
    taxStatus: '508(c)(1)(a)'
  },
  { 
    id: 'special', 
    name: 'Special Projects', 
    nameEs: 'Proyectos Especiales',
    icon: Sparkles, 
    description: 'New initiatives & programs',
    color: 'bg-gold/20 text-gold',
    org: 'lfm',
    taxStatus: '508(c)(1)(a)'
  },
];

export default function DonatePage() {
  const { t, i18n } = useTranslation();
  const [amount, setAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [selectedMinistries, setSelectedMinistries] = useState(['general']);
  const [displayAnonymous, setDisplayAnonymous] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    city: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleAmountSelect = (value) => {
    setAmount(value);
    setCustomAmount('');
  };

  const handleCustomAmount = (e) => {
    const value = e.target.value;
    setCustomAmount(value);
    if (value) {
      setAmount(parseFloat(value) || 0);
    }
  };

  const toggleMinistry = (ministryId) => {
    setSelectedMinistries(prev => {
      if (prev.includes(ministryId)) {
        // Don't allow deselecting if it's the only one
        if (prev.length === 1) return prev;
        return prev.filter(id => id !== ministryId);
      } else {
        return [...prev, ministryId];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Determine if using preset package or custom amount
      const isPresetAmount = donationAmounts.includes(amount) && !customAmount;
      
      const payload = {
        origin_url: window.location.origin,
        donor_name: formData.name,
        donor_email: formData.email,
        donor_city: formData.city,
        ministries: selectedMinistries,
        message: formData.message,
        display_anonymous: displayAnonymous
      };
      
      if (isPresetAmount) {
        payload.package_id = amount.toString();
      } else {
        payload.custom_amount = parseFloat(amount);
      }
      
      // Create Stripe checkout session
      const response = await axios.post(`${API}/payments/donate/checkout`, payload);
      
      if (response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Donation error:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
      setIsSubmitting(false);
    }
  };

  // Check for return from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    
    // Check if we're on the success path (handles both /donate/success and /donate with session_id)
    const isSuccessPath = window.location.pathname.includes('/donate/success') || sessionId;
    
    if (sessionId) {
      // Poll for payment status
      const pollStatus = async (attempts = 0) => {
        try {
          const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
          
          if (response.data.payment_status === 'paid') {
            setAmount(response.data.amount_total);
            setSubmitted(true);
            // Clear the URL parameters
            window.history.replaceState({}, '', '/donate');
          } else if (response.data.status === 'expired' || attempts >= 5) {
            toast.error('Payment session expired. Please try again.');
            window.history.replaceState({}, '', '/donate');
          } else {
            // Continue polling
            setTimeout(() => pollStatus(attempts + 1), 2000);
          }
        } catch (error) {
          console.error('Error checking payment status:', error);
          if (attempts < 3) {
            setTimeout(() => pollStatus(attempts + 1), 2000);
          }
        }
      };
      
      pollStatus();
    }
  }, []);

  const isSpanish = i18n.language === 'es';

  if (submitted) {
    const selectedNames = selectedMinistries.map(id => {
      const ministry = MINISTRY_OPTIONS.find(m => m.id === id);
      return isSpanish ? ministry?.nameEs : ministry?.name;
    }).join(', ');

    // Get tax status for selected ministries
    const selectedOrgs = selectedMinistries.map(id => {
      const ministry = MINISTRY_OPTIONS.find(m => m.id === id);
      return ministry?.org;
    });
    const taxStatusText = selectedOrgs.includes('outreach') 
      ? 'Living Fruit Outreach | 501(c)(3)'
      : 'Living Fruit Ministry, Inc. | 508(c)(1)(a)';

    return (
      <div className="min-h-screen bg-cream pt-24 flex items-center justify-center" data-testid="donate-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md mx-auto px-6"
        >
          <div className="w-20 h-20 bg-olive rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-white" />
          </div>
          <h1 className="font-serif text-3xl text-charcoal mb-4">{t('donate.thankYou', 'Thank You!')}</h1>
          <p className="text-charcoal/70 mb-4">
            Your generous gift of <span className="text-burgundy font-semibold">${amount}</span> will make a difference!
          </p>
          <p className="text-sm text-charcoal/60 mb-6">
            <strong>Designated to:</strong> {selectedNames}
          </p>
          <p className="text-sm text-charcoal/60 mb-8 bg-green-50 p-3 rounded-xl border border-green-200">
            A receipt will be sent to your email. <br />
            <span className="text-xs">{taxStatusText}</span>
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setFormData({ name: '', email: '', city: '', message: '' });
              setSelectedMinistries(['general']);
              setAmount(50);
              setDisplayAnonymous(false);
            }}
            className="bg-burgundy hover:bg-purple text-white"
          >
            {t('donate.makeAnother', 'Make Another Donation')}
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pt-20" data-testid="donate-page">
      {/* Scripture Header */}
      <ScriptureHeader page="donate" />

      {/* Hero */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-16 h-16 bg-burgundy rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart size={32} className="text-white" />
            </div>
            <span className="text-xs uppercase tracking-[0.2em] text-charcoal mb-4 block">{t('donate.supportMission', 'Support Our Mission')}</span>
            <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-4">{t('donate.title', 'Give Today')}</h1>
            <p className="text-charcoal/70 max-w-2xl mx-auto">
              {t('donate.subtitle', 'Your generosity helps us serve families, spread the Gospel, and make a lasting impact in our community.')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Fruit Bearers Banner */}
      <section className="py-6 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.a
            href="/fruit-bearers"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="block bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-6 text-white hover:scale-[1.02] transition-transform cursor-pointer"
            data-testid="fruit-bearers-banner"
          >
            <div className="flex items-center gap-4">
              <span className="text-4xl">🍎</span>
              <div className="flex-1">
                <h3 className="font-serif text-xl mb-1">Become a Fruit Bearer</h3>
                <p className="text-white/80 text-sm">
                  Join our monthly giving family starting at just $10/month. Your consistent support keeps the mission growing!
                </p>
              </div>
              <span className="text-2xl">→</span>
            </div>
          </motion.a>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-6">
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-gold/10"
            data-testid="donation-form"
          >
            {/* Secure Payment Notice */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8 text-center">
              <p className="text-sm text-green-800">
                <CreditCard size={16} className="inline mr-2" />
                <strong>Secure Payment via Stripe</strong>
              </p>
              <p className="text-xs text-green-700 mt-1">
                Living Fruit Ministry | 508(c)(1)(a) Religious Organization
              </p>
            </div>

            {/* Ministry Selection */}
            <div className="mb-8">
              <Label className="text-charcoal font-medium mb-2 block text-lg">
                {isSpanish ? '¿A qué ministerio desea donar?' : 'Which ministry would you like to support?'}
              </Label>
              <p className="text-sm text-charcoal/60 mb-4">
                {isSpanish ? 'Seleccione uno o más ministerios' : 'Select one or more ministries'}
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {MINISTRY_OPTIONS.map((ministry) => {
                  const Icon = ministry.icon;
                  const isSelected = selectedMinistries.includes(ministry.id);
                  return (
                    <button
                      key={ministry.id}
                      type="button"
                      onClick={() => toggleMinistry(ministry.id)}
                      className={`relative p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                        isSelected 
                          ? 'border-burgundy bg-burgundy/5 shadow-md' 
                          : 'border-gold/20 bg-cream hover:border-gold/40 hover:bg-gold/5'
                      }`}
                      data-testid={`ministry-${ministry.id}`}
                    >
                      {/* Checkbox indicator */}
                      <div className={`absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center ${
                        isSelected ? 'bg-burgundy' : 'bg-gray-200'
                      }`}>
                        {isSelected && <CheckCircle size={14} className="text-white" />}
                      </div>
                      
                      <div className={`w-10 h-10 rounded-lg ${ministry.color} flex items-center justify-center mb-2`}>
                        <Icon size={20} />
                      </div>
                      <h3 className="font-medium text-charcoal text-sm leading-tight">
                        {isSpanish ? ministry.nameEs : ministry.name}
                      </h3>
                      <p className="text-xs text-charcoal/50 mt-1 leading-tight">
                        {ministry.description}
                      </p>
                    </button>
                  );
                })}
              </div>
              
              {/* Selected summary */}
              {selectedMinistries.length > 0 && (
                <div className="mt-4 p-3 bg-olive/10 rounded-xl">
                  <p className="text-sm text-olive">
                    <CheckCircle size={14} className="inline mr-1" />
                    <strong>{isSpanish ? 'Seleccionado:' : 'Selected:'}</strong>{' '}
                    {selectedMinistries.map(id => {
                      const ministry = MINISTRY_OPTIONS.find(m => m.id === id);
                      return isSpanish ? ministry?.nameEs : ministry?.name;
                    }).join(', ')}
                  </p>
                </div>
              )}
            </div>

            {/* Amount Selection */}
            <div className="mb-8">
              <Label className="text-charcoal font-medium mb-4 block">{t('donate.selectAmount', 'Select Amount')}</Label>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-4">
                {donationAmounts.map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleAmountSelect(value)}
                    className={`py-3 px-4 rounded-xl font-medium transition-all duration-300 ${
                      amount === value && !customAmount
                        ? 'bg-burgundy text-white'
                        : 'bg-cream text-charcoal hover:bg-gold/20'
                    }`}
                    data-testid={`amount-${value}`}
                  >
                    ${value}
                  </button>
                ))}
              </div>
              <div className="relative">
                <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/40" size={18} />
                <Input
                  type="number"
                  placeholder={t('donate.customAmount', 'Custom amount')}
                  value={customAmount}
                  onChange={handleCustomAmount}
                  className="pl-10 bg-cream border-gold/20 focus:border-gold"
                  data-testid="custom-amount"
                />
              </div>
            </div>

            {/* Recurring Option */}
            <div className="mb-8">
              <label className="flex items-center gap-3 cursor-pointer p-4 bg-cream rounded-xl border border-gold/20 hover:border-gold/40 transition-colors">
                <input
                  type="checkbox"
                  checked={isRecurring}
                  onChange={(e) => setIsRecurring(e.target.checked)}
                  className="w-5 h-5 rounded border-gold/30 text-burgundy focus:ring-gold"
                  data-testid="recurring-checkbox"
                />
                <div>
                  <span className="text-charcoal font-medium">{t('donate.recurringMonthly', 'Make this a monthly donation')}</span>
                  <p className="text-xs text-charcoal/60">{isSpanish ? 'Se procesará automáticamente cada mes' : 'Automatically processed each month'}</p>
                </div>
              </label>
            </div>

            {/* Personal Info */}
            <div className="space-y-4 mb-8">
              <div>
                <Label>{t('donate.fullName', 'Full Name')}</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="bg-cream border-gold/20 focus:border-gold"
                  data-testid="donor-name"
                />
              </div>
              <div>
                <Label>{t('donate.email', 'Email')}</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="bg-cream border-gold/20 focus:border-gold"
                  data-testid="donor-email"
                />
              </div>
              <div>
                <Label>{t('donate.city', 'City')}</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  placeholder={isSpanish ? 'Tu ciudad' : 'Your city'}
                  className="bg-cream border-gold/20 focus:border-gold"
                  data-testid="donor-city"
                />
              </div>
              <div>
                <Label>{t('donate.messageOptional', 'Message (Optional)')}</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder={isSpanish ? 'Comparta una nota o petición de oración...' : 'Share a note or prayer request...'}
                  rows={3}
                  className="bg-cream border-gold/20 focus:border-gold"
                  data-testid="donor-message"
                />
              </div>
            </div>

            {/* Anonymous Option */}
            <div className="bg-cream rounded-xl p-4 border border-gold/20">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={displayAnonymous}
                  onChange={(e) => setDisplayAnonymous(e.target.checked)}
                  className="w-5 h-5 mt-0.5 rounded border-gold/30 text-burgundy focus:ring-burgundy"
                  data-testid="anonymous-checkbox"
                />
                <div>
                  <div className="flex items-center gap-2 font-medium text-charcoal">
                    {displayAnonymous ? <EyeOff size={16} /> : <Eye size={16} />}
                    {isSpanish ? 'Mostrar como Anónimo' : 'Display as Anonymous'}
                  </div>
                  <p className="text-xs text-charcoal/60 mt-1">
                    {isSpanish 
                      ? 'Su información se guardará de forma privada, pero su donación se mostrará como "Anónimo" públicamente.'
                      : 'Your information will be saved privately, but your donation will display as "Anonymous" publicly.'}
                  </p>
                </div>
              </label>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={isSubmitting || amount <= 0}
              className="w-full bg-burgundy hover:bg-purple text-white py-6 text-lg font-medium rounded-xl"
              data-testid="submit-donation"
            >
              {isSubmitting ? t('donate.processing', 'Processing...') : `${t('donate.donateButton', 'Donate')} $${amount}`}
            </Button>

            {/* Security Note */}
            <p className="text-center text-xs text-charcoal/50 mt-6">
              <CreditCard size={14} className="inline mr-1" />
              {t('donate.secureNote', 'Secure donation • Tax-deductible')}
            </p>
          </motion.form>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">{t('donate.yourImpact', 'Your Impact')}</span>
            <h2 className="font-serif text-3xl text-charcoal">{t('donate.howGiftHelps', 'How Your Gift Helps')}</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { amount: '$25', impact: 'Provides groceries for a family for one week', icon: Utensils },
              { amount: '$50', impact: 'Helps rescue and care for an animal in need', icon: PawPrint },
              { amount: '$100', impact: 'Supports a missionary for one day', icon: Globe },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-6 bg-cream rounded-2xl"
              >
                <item.icon size={32} className="text-gold mx-auto mb-4" />
                <span className="block font-serif text-3xl text-burgundy mb-2">{item.amount}</span>
                <p className="text-charcoal/70 text-sm">{item.impact}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ministry Highlights */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">
              {isSpanish ? 'NUESTROS MINISTERIOS' : 'OUR MINISTRIES'}
            </span>
            <h2 className="font-serif text-3xl text-charcoal">
              {isSpanish ? 'Dónde Va Su Donación' : 'Where Your Donation Goes'}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: isSpanish ? 'Despensa de Alimentos' : 'Food Pantry',
                description: isSpanish 
                  ? 'Cada semana servimos a más de 100 familias con alimentos nutritivos, productos frescos y artículos esenciales.'
                  : 'Every week we serve 100+ families with nutritious food, fresh produce, and essential items.',
                icon: Utensils,
                stat: '500+',
                statLabel: isSpanish ? 'familias al mes' : 'families monthly'
              },
              {
                title: isSpanish ? 'Ofrenda Semanal' : 'Weekly Offering',
                description: isSpanish
                  ? 'Sus diezmos y ofrendas fieles sostienen todas las operaciones del ministerio y nos permiten servir a nuestra comunidad.'
                  : 'Your faithful tithes and offerings sustain all ministry operations and enable us to serve our community.',
                icon: Gift,
                stat: '100%',
                statLabel: isSpanish ? 'va al ministerio' : 'goes to ministry'
              },
              {
                title: isSpanish ? 'Ministerio de Medios' : 'Media Ministry',
                description: isSpanish
                  ? 'Producimos videos, podcasts y transmisiones en vivo para alcanzar personas en todo el mundo con el Evangelio.'
                  : 'We produce videos, podcasts, and livestreams to reach people worldwide with the Gospel.',
                icon: Video,
                stat: '1000+',
                statLabel: isSpanish ? 'vistas semanales' : 'weekly views'
              },
              {
                title: isSpanish ? 'Misiones Globales' : 'Global Missions',
                description: isSpanish
                  ? 'Apoyamos misioneros en América Latina, África y Asia con oraciones, recursos y viajes misioneros.'
                  : 'We support missionaries in Latin America, Africa, and Asia with prayer, resources, and mission trips.',
                icon: Globe,
                stat: '12',
                statLabel: isSpanish ? 'misioneros apoyados' : 'missionaries supported'
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-2xl border border-gold/10 hover:border-gold/30 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-burgundy/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon size={24} className="text-burgundy" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif text-lg text-charcoal mb-2">{item.title}</h3>
                    <p className="text-charcoal/70 text-sm mb-3">{item.description}</p>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-burgundy">{item.stat}</span>
                      <span className="text-sm text-charcoal/60">{item.statLabel}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
